import {
  Area,
  Bar,
  BarConfig,
  Column,
  ColumnConfig,
  G2,
  Line,
  LineConfig,
  Pie
} from '@ant-design/charts';
import { wilkinsonExtended } from '@antv/scale';
import { PivotConfig, Query, ResultSet } from '@cubejs-client/core';
import { formatDate, formatDateAndTime, useTranslation } from '@threatadvisor/react-utils';
import { Alert, Table } from 'antd';
import { format, isValid, parseISO } from 'date-fns';
import React, { CSSProperties, ReactElement, useContext } from 'react';
import ErrorBoundary from './ErrorBoundary';
import GridTable from './GridTable';

import { CubeContext, QueryRenderer } from '@cubejs-client/react';
import meanBy from 'lodash-es/meanBy';
import CustomTheme from './g2-theme';
G2.register('theme.threatadvisor', CustomTheme);

//import { ContainerProps } from '@ant-design/charts/es/hooks/useChart';
// Leaving ContainerProps definition here for reference
// export interface ContainerProps {
//   style?: React.CSSProperties;
//   className?: string;
//   loading?: boolean;
//   loadingTemplate?: React.ReactElement;
//   errorTemplate?: (e: Error) => React.ReactNode;
//   onReady?: (chart: G2PlotConfig) => void;
//   onEvent?: (chart: G2PlotConfig, event: G2.Event) => void;
// }

function getColorForSeverityText(severityText: string): CSSProperties['color'] | undefined {
  if (severityText.toLowerCase().includes('critical')) return '#403d3c';
  if (severityText.toLowerCase().includes('high')) return '#f94615';
  if (severityText.toLowerCase().includes('medium')) return '#ffba02';
  if (severityText.toLowerCase().includes('low')) return '#257dd0';
  if (severityText.toLowerCase().includes('informational')) return '#5a8d17';
}

function getNumberForSeverityText(severityText: string): number {
  if (severityText.toLowerCase().includes('critical')) return 5;
  if (severityText.toLowerCase().includes('high')) return 4;
  if (severityText.toLowerCase().includes('medium')) return 3;
  if (severityText.toLowerCase().includes('low')) return 2;
  if (severityText.toLowerCase().includes('informational')) return 1;
  return 0;
}

// These priorities are specific to the Security Event domain
// They now exist in the domain utils however they are currently dependencies here
// remove the export for now and only use the exported members from the domain utils
function getPriorityColors(priorty: string): any {
  switch (priorty) {
    case 'P1':
      return '#800020';
    case 'P2':
      return '#FF5D00';
    case 'P3':
      return '#ffc104';
    default:
      // default color, ie P4
      return '#4572c5';
  }
}

function formatDateAxisLabel(x: string, granularity: string) {
  try {
    const day = parseISO(x);
    if (isValid(day)) {
      switch (granularity) {
        case 'day':
        case 'week':
          return formatDate(day);
        case 'month':
          return format(day, 'MMM yy');
        case 'year':
          return format(day, 'yyyy');
        default:
          return formatDateAndTime(day);
      }
    }
  } catch (e) {
    console.log(x, e);
  }
  return x;
}

const TypeToChartComponent: Record<
  string,
  (props: {
    resultSet: ResultSet;
    pivotConfig?: PivotConfig;
    config: any;
    t: (val: string) => string; // changed to support multiple translation files
    hideCubeName?: boolean;
    useGridTable?: boolean;
    noTitleTranslation?: boolean;
    useHeightBasedOnHowManyItems?: boolean;
    wrapCategoryAxisLabel?: boolean;
    onRender?: () => void;
    showZeroCategories?: string[];
    showZerosStacked?: string[];
  }) => ReactElement
> = {
  line: ({ resultSet, pivotConfig, config, t, hideCubeName }) => {
    const annotations = resultSet.annotation();
    const getAnnotation = key => (annotations.dimensions[key] ?? annotations.measures[key])?.title;

    const timeGranularity =
      resultSet.query()?.timeDimensions && resultSet.query().timeDimensions![0]
        ? resultSet.query().timeDimensions![0].granularity
        : undefined;

    const results = resultSet.chartPivot(pivotConfig);
    const data: { x: string | Date; y: number; series: string }[] = [];
    resultSet.seriesNames(pivotConfig).forEach(({ key, yValues }) => {
      //console.log(key, yValues, results);
      data.push(
        ...results.map(result => ({
          x:
            config.scale?.x?.type == 'time'
              ? formatDateAxisLabel(result.xValues[0], timeGranularity ?? 'day')
              : result.xValues[0],
          y: result[key],
          series: yValues[0] && yValues[0].length > 0 ? t(yValues[0]) : 'Undefined'
        }))
      );
    });
    const configExtended: LineConfig = {
      ...config,
      xField: 'x',
      yField: 'y',
      seriesField: 'series',
      colorField: config.colorField ?? 'series',
      theme: { type: 'threatadvisor' },
      scale: {
        x: {
          nice: true
        },
        y: {
          type: 'linear',
          tickMethod: wilkinsonExtended
        }
      },
      label: {
        // AT EACH Y POINT
        formatter: val => (val === 0 ? '' : val)
        // textAnchor: 'middle',
        // position: 'inside',
        // style: {
        //   textAnchor: (d) => (+d.frequency > 0.008 ? 'right' : 'start'),
        //   fill: (d) => (+d.frequency > 0.008 ? '#fff' : '#000'),
        //   dx: (d) => (+d.frequency > 0.008 ? -5 : 5),
        // },
      }
    };

    const myId = resultSet['loadResponse']['results'][0]['requestId'];
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';

    return (
      <Line
        {...configExtended}
        data={data}
        onReady={plot => {
          plot.chart.on('afterrender', args => {
            if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
          });
        }}
      />
    );
  },
  bar: ({
    resultSet,
    pivotConfig,
    config,
    t,
    hideCubeName,
    noTitleTranslation,
    useHeightBasedOnHowManyItems,
    wrapCategoryAxisLabel,
    onRender,
    showZeroCategories,
    showZerosStacked
  }) => {
    const annotations = resultSet.annotation();
    const getAnnotation = key => (annotations.dimensions[key] ?? annotations.measures[key])?.title;

    const timeGranularity =
      resultSet.query()?.timeDimensions && resultSet.query().timeDimensions![0]
        ? resultSet.query().timeDimensions![0].granularity
        : undefined;
    const results = resultSet.chartPivot(pivotConfig);
    const data: { x: string | Date; y: number; series: string; color?: string }[] = [];

    resultSet.seriesNames(pivotConfig).forEach(({ key, yValues }) => {
      //console.log(key, results);
      data.push(
        ...results.map(result => ({
          x:
            config.scale?.x?.type == 'time'
              ? formatDateAxisLabel(result.xValues[0], timeGranularity ?? 'day')
              : result.xValues[0]
                ? t(result.xValues[0])
                : 'Undefined', //renderTimeIfNeeded(result.xValues[0], resultSet, t),
          y: result[key],
          series: t(yValues[0]) ?? 'Undefined',
          ...(key.includes('severity')
            ? { color: getColorForSeverityText(key) }
            : pivotConfig?.y?.some(d => d.endsWith('priority'))
              ? { color: getPriorityColors(yValues[0]) } // this does not work
              : {})
        }))
      );
    });

    if (showZeroCategories) {
      const seriesName =
        resultSet.seriesNames(pivotConfig)[0] && t(resultSet.seriesNames(pivotConfig)[0].yValues[0])
          ? t(resultSet.seriesNames(pivotConfig)[0].key)
          : '';
      for (let i = 0; i < showZeroCategories.length; i++) {
        if (!data.find(e => e.x === showZeroCategories[i])) {
          data.push({ x: showZeroCategories[i], y: 0, series: seriesName });
        }
      }

      data.sort((a, b) => {
        //x value is either a date or a string
        const aValue: any = typeof a.x === 'object' ? a.x.getTime() : a.x;
        const bValue: any = typeof b.x === 'object' ? b.x.getTime() : b.x;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return aValue.localeCompare(bValue);
        } else {
          return aValue - bValue; //numeric comparison for timestamps
        }
      });
    }

    if (showZerosStacked) {
      //find distinct values of x - x is our category here
      const distinctCategories = data
        .filter((obj, index, self) => self.findIndex(o => o['x'] === obj['x']) === index)
        .map(obj => obj['x']);

      for (let categoryIndex = 0; categoryIndex < distinctCategories.length; categoryIndex++) {
        //for each distinct category, try to find the 'series' we want to always show
        for (let seriesIndex = 0; seriesIndex < showZerosStacked.length; seriesIndex++) {
          //if we don't find data with the same category and series, then we assume there is a 0 count for this data
          if (
            !data.find(
              piece =>
                piece.series === showZerosStacked[seriesIndex] &&
                piece.x === distinctCategories[categoryIndex]
            )
          ) {
            //append the info we want
            data.push({
              x: distinctCategories[categoryIndex],
              y: 0,
              series: showZerosStacked[seriesIndex]
            });
          }
        }
      }

      data.sort((a, b) => {
        //sort by the x field (category) first
        if (a.x < b.x) {
          return -1;
        }
        if (a.x > b.x) {
          return 1;
        }

        //if category is equal, sort by series name
        if (a.series < b.series) {
          return -1;
        }
        if (a.series > b.series) {
          return 1;
        }

        //both are equal
        return 0;
      });
    }

    const configExtended: BarConfig = {
      ...config, // allow specific config items to override
      ...(useHeightBasedOnHowManyItems ? { height: data.length * 40 } : { autofit: true }),
      style: {
        ...config.style,
        maxWidth: 25
      },
      label: {
        // AT EACH Y POINT
        formatter: val => (val === 0 && !showZeroCategories ? '' : val),
        textAnchor: 'start',
        position: 'right'
      },
      axis: {
        // ACROSS THE X Axis... vertical in this case
        x: {
          labelFormatter: (text: string, item: any, index: number) => text ?? 'Undefined',
          ...(wrapCategoryAxisLabel
            ? {
                size: 200,
                labelAutoWrap: {
                  type: 'wrap',
                  wordWrapWidth: 200, //number,
                  maxLines: 2,
                  recoverWhenFailed: false
                }
              }
            : {})
        },
        y: {
          grid: true
        }
      },
      scale: {
        ...config.scale,
        y: {
          type: 'linear',
          reverse: true,
          tickMethod: wilkinsonExtended
        }
      },
      colorField: config.colorField ?? 'x',
      theme: { type: 'threatadvisor' },
      ...(config.stack ? {} : { seriesField: 'series' })
    };

    const myId = resultSet['loadResponse']['results'][0]['requestId'];

    if (data.length == 0) return <></>;

    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';

    return (
      // <pre>{JSON.stringify(data, null, 2)}</pre>

      <Bar
        {...configExtended}
        data={data}
        xField="x"
        yField="y"
        onReady={plot => {
          plot.chart.on('afterrender', args => {
            if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
          });
        }}
      />
    );
  },
  column: ({ resultSet, pivotConfig, config, t, hideCubeName, showZeroCategories }) => {
    const annotations = resultSet.annotation();
    const getAnnotation = key => (annotations.dimensions[key] ?? annotations.measures[key])?.title;

    const results = resultSet.chartPivot(pivotConfig);
    const data: { x: string | Date; y: number; series: string }[] = [];
    resultSet.seriesNames(pivotConfig).forEach(({ key, yValues }) => {
      //console.log(key, results, yValues);
      data.push(
        ...results.map(result => ({
          x:
            config.scale?.x.type == 'time'
              ? new Date(result.xValues[0])
              : result.xValues[0]
                ? t(result.xValues[0])
                : 'Undefined',
          y: result[key],
          series: yValues[0] ? t(yValues[0]) : 'Undefined',
          ...(key.includes('severity') ? { color: getColorForSeverityText(key) } : {})
        }))
      );
    });
    if (showZeroCategories) {
      const seriesName = '';
      for (let i = 0; i < showZeroCategories.length; i++) {
        if (!data.find(e => e.x === showZeroCategories[i])) {
          data.push({ x: showZeroCategories[i], y: 0, series: seriesName });
        }
      }
    }

    const configExtended: ColumnConfig = {
      ...config, // allow specific config items to override
      style: {
        ...config.style,
        maxWidth: 25
      },
      label: {
        // AT EACH Y POINT
        text: val => (val.y === 0 ? '' : val.y),
        textBaseline: 'bottom'
      },
      axis: {
        // ACROSS THE X Axis... vertical in this case
        x: {
          labelFormatter: (text: string, item: any, index: number) => {
            //console.log(text, item, index);
            return text ?? 'Undefined';
          }
        }
      },
      scale: {
        //...config.scale,
        y: {
          type: 'linear',
          reverse: true,
          tickMethod: wilkinsonExtended
        }
      },
      colorField: config.colorField ?? 'x',
      theme: { type: 'threatadvisor' },
      ...(config.stack ? {} : { seriesField: 'series' })
    };

    const myId = resultSet['loadResponse']['results'][0]['requestId'];

    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';

    return (
      <Column
        {...configExtended}
        data={data}
        xField="x"
        yField="y"
        seriesField="series"
        onReady={plot => {
          plot.chart.on('afterrender', args => {
            if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
          });
        }}
      />
    );
  },
  area: ({ resultSet, pivotConfig, config, t, hideCubeName }) => {
    const results = resultSet.chartPivot(pivotConfig);
    const data: { y: number; x: string | Date; series: string }[] = [];
    resultSet.seriesNames(pivotConfig).forEach(({ key, yValues }) => {
      data.push(
        ...results.map(result => ({
          x:
            config.scale?.x.type == 'time'
              ? new Date(result.xValues[0])
              : result.xValues[0]
                ? t(result.xValues[0])
                : 'Undefined',
          y: result[key],
          series: t(yValues[0]) ?? 'Undefined'
        }))
      );
    });

    const myId = resultSet['loadResponse']['results'][0]['requestId'];
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';

    return (
      <Area
        {...config}
        data={data}
        xField="x"
        yField="y"
        seriesField="series"
        isStack={false}
        onReady={plot => {
          plot.chart.on('afterrender', args => {
            if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
          });
        }}
      />
    );
  },
  pie: ({ resultSet, pivotConfig, config, t }) => {
    const angleField = resultSet.seriesNames(pivotConfig)[0]?.key;
    if (!angleField) {
      if (resultSet.query().dimensions?.length) {
        return <Alert type="warning" message={t('errors.noData')} />;
      }
      return <Alert type="error" message={t('missingDimension')} />;
    }

    const myId = resultSet['loadResponse']['results'][0]['requestId'];
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';

    return (
      <Pie
        {...config}
        data={resultSet.chartPivot(pivotConfig)}
        colorField="x"
        angleField={angleField}
        label={{ type: 'inner', content: '{name}: {value}' }}
        onReady={plot => {
          plot.chart.on('afterrender', args => {
            if (window['chartRenders']) window['chartRenders'][myId] = 'Done';
          });
        }}
      />
    );
  },
  number: ({ resultSet, config }) => {
    return (
      <>
        {resultSet.seriesNames().map((s, i) => {
          const dataSeries = resultSet.series()[i].series;
          let val = resultSet.chartPivot()[0][s.key];
          if (config.aggregate) val = meanBy(dataSeries, 'value').toFixed();

          if (config.labelFormatter) val = config.labelFormatter(val);

          return <span key={s.key}>{val}</span>;
        })}
      </>
    );
  },
  table: ({ resultSet, pivotConfig, t, hideCubeName, useGridTable }) => {
    const columns = resultSet.tableColumns(pivotConfig);

    // Optimize single-column tables by showing in a grid to save space
    if (columns.length === 1 && useGridTable) {
      return (
        <GridTable
          content={resultSet
            .tablePivot(pivotConfig)
            .map(instance => instance[columns[0].dataIndex]?.toString())}
          header={t(columns[0].dataIndex)}
        />
      );
    }

    return (
      <Table
        className="compact"
        rowKey={() => {
          return Math.random().toString(36).slice(2, 7);
        }}
        pagination={false}
        columns={resultSet.tableColumns(pivotConfig).map(column => ({
          ...column,
          title: t(column.dataIndex)
        }))}
        dataSource={resultSet.tablePivot(pivotConfig)}
      />
    );
  }
};
const TypeToMemoChartComponent = Object.keys(TypeToChartComponent)
  .map(key => ({
    [key]: React.memo(TypeToChartComponent[key])
  }))
  .reduce((a, b) => ({ ...a, ...b }));

export type ChartType = 'line' | 'bar' | 'table' | 'area' | 'number' | 'pie' | 'column';

export type VizState = {
  query?: Query;
  pivotConfig?: PivotConfig;
  chartType?: ChartType;
};

type ChartProps = {
  config: VizState;
  hideCubeName?: boolean;
  useGridTable?: boolean;
  chartConfig?: any;
  noTitleTranslation?: boolean;
  useHeightBasedOnHowManyItems?: boolean;
  wrapCategoryAxisLabel?: boolean;
  onRender?: () => void;
  showZeroCategories?: string[];
  showZerosStacked?: string[];
};

/*
 * Chart component that can render queries built using the QueryBuilder component
 *  - `config`: Sent to cubejs server to get results, and used to know how to render results
 *  - `chartConfig`: Allows customization of Ant Design charts (passed through to underlying component)
 *  - `hideCubeName`: Boolean value that controls if the name of the cube is shown, or just the name of the properties
 *  - `useGridTable`: Boolean value that signifies that the GridTable component should be used to render single-column tables, instead of the normal table component
 *  - `noTitleTranslation`: Boolean value that deteremines if the title of the Y column should use internationlization
 *  - `useHeightBasedOnHowManyItems`: Boolean value that will try to use the length of the data to determine height of the graph
 *  - `wrapCategoryAxisLabel`: Boolean value which will try to wrap the categorical axis Label to two lines, and if larger than two lines, will ellipses
 *  - `showZeroCategories`: String array - values in here will be displayed in regular bar charts even if the total count is 0
 *  - `showZerosStacked`: String array - values in here will be displayed in stacked bar charts even if there is no result
 */
export default function Chart(props: ChartProps): ReactElement | null {
  const { config, ...innerProps } = props;
  const { query, chartType, pivotConfig } = config;

  const { t } = useTranslation('reports');
  const { cubeApi } = useContext(CubeContext);

  const Component = TypeToMemoChartComponent[chartType ?? 'table'];

  return (
    <ErrorBoundary fallback={e => <Alert type="error" message={e.message} />}>
      <QueryRenderer
        cubeApi={cubeApi}
        query={query!}
        render={props => {
          const { resultSet, loadingState } = props;

          if (loadingState.isLoading !== undefined && loadingState.isLoading === false && resultSet)
            return (
              <Component
                {...innerProps}
                //noTitleTranslation={noTitleTranslation}
                // hideCubeName={hideCubeName}
                // useGridTable={useGridTable}
                // useHeightBasedOnHowManyItems={useHeightBasedOnHowManyItems}
                // wrapCategoryAxisLabel={wrapCategoryAxisLabel}

                // Mount new component on query change ????
                key={JSON.stringify(query)}
                resultSet={resultSet}
                pivotConfig={pivotConfig}
                config={innerProps.chartConfig ?? {}}
                t={t}
              />
            );
        }}
      />
    </ErrorBoundary>
  );
}
